// Transformacion y formato de los graficos del Engine. El modelo (backend/services/engine_charts.py) trae solo
// numeros y claves, ya ordenados; aqui se decide la escala, la posicion de cada marca y como se escribe cada cifra
// en cada idioma. Todo recibe `lang` explicito: el mismo modelo se pinta igual en es y en, solo cambia el formato.
import i18n from "@/i18n";
import { LOCALE_BY_LANG } from "@/i18n/languages";

// Unico nucleo de formato de numeros del Engine: los helpers de lib/engine.js (fmtPct, formatGap...) y las fechas de
// lib/engineQuantum.js delegan aqui, para que una misma cifra se escriba igual en todas las paginas.
export const MINUS = "−";

const norm = (lang) => (lang === "en" ? "en" : "es");
const tFor = (lang) => i18n.getFixedT(norm(lang), "engine");

const numberOptions = ({ min = 0, max = 1, sig }) => (sig
    ? { maximumSignificantDigits: sig }
    : { minimumFractionDigits: min, maximumFractionDigits: Math.max(min, max) });

// Dos estilos. Por defecto (graficos y PDF), siempre agrupado y con el menos tipografico: el es de Intl no agrupa los
// miles con cuatro cifras («1234 €») y la maqueta escribe «1.234 €», asi que se formatea en en-US y en es se
// intercambian los separadores; el signo va aparte y nunca sale un «−0». Con `grouping: "locale"` es exactamente el
// toLocaleString que usaban las paginas del Engine antes de los graficos, signo incluido: con el, su texto en espanol
// no cambia ni un byte.
function signed(v, lang, opts = {}) {
    const n = Number(v);
    if (opts.grouping === "locale") {
        return { sign: "", body: new Intl.NumberFormat(LOCALE_BY_LANG[norm(lang)], numberOptions(opts)).format(n) };
    }
    const abs = Math.abs(n);
    const s = new Intl.NumberFormat("en-US", { ...numberOptions(opts), useGrouping: true }).format(abs);
    const body = norm(lang) === "en" ? s : s.replace(/[,.]/g, (c) => (c === "," ? "." : ","));
    const zero = opts.sig ? abs === 0 : Number(abs.toFixed(Math.max(opts.min ?? 0, opts.max ?? 1))) === 0;
    return { sign: zero ? "" : n < 0 ? MINUS : opts.plus ? "+" : "", body };
}

const withUnit = (lang, unit, { sign, body: b }) => `${sign}${tFor(lang)(`charts.unit.${unit}`, { n: b })}`;

export function fmtNumber(v, lang, opts = {}) {
    const { sign, body: b } = signed(v, lang, opts);
    return `${sign}${b}`;
}

export const fmtPct = (v, lang, opts = {}) => withUnit(lang, "pct", signed(v, lang, { min: 0, max: 1, ...opts }));

// Decimales para `v`: `max`, o los que hagan falta (hasta 4, la precision del modelo) para que un dato distinto de
// cero no se lea «0»: una operacion de 0,04 pp no puede salir como «0 pp».
export function visibleDigits(v, max = 1) {
    const abs = Math.abs(Number(v));
    let d = max;
    while (d < 4 && abs > 0 && Number(abs.toFixed(d)) === 0) d += 1;
    return d;
}

export const fmtPp = (v, lang, { max = 1 } = {}) => withUnit(lang, "pp", signed(v, lang, { max: visibleDigits(v, max), plus: true }));

// Un peso en %, con la misma regla: un 0,03 % no se lee «0 %». Como fmt_weight de backend/services/engine_pdf_charts.py.
export const fmtWeight = (v, lang, { max = 1 } = {}) => fmtPct(v, lang, { max: visibleDigits(v, max) });

// Las tablas de «Ver tabla» sustituyen a las listas de antes de los graficos: con su misma precision (hasta 2
// decimales); los graficos se quedan con 1.
export const TABLE_DIGITS = 2;

export const fmtEur = (v, lang, { signed: plus = false } = {}) => withUnit(lang, "eur", signed(v, lang, { max: 0, plus }));

export const fmtBps = (v, lang, opts = {}) => withUnit(lang, "bps", signed(v, lang, { min: 1, max: 1, ...opts }));

// Precision de la brecha de la cota: dos decimales por debajo de 10 pb, uno por encima. La comparten formatGap y
// QualityBound.
export const gapDigits = (bps) => (Number(bps) < 10 ? 2 : 1);
export const fmtGapBps = (bps, lang) => fmtBps(bps, lang, { min: 0, max: gapDigits(bps) });

// Probabilidad en [0, 1] como %: con un decimal, o dos por debajo del 1 % (el azar con 12 activos es un 0,11 %).
export function fmtProb(p, lang) {
    const pct = Number(p) * 100;
    const d = pct > 0 && pct < 1 ? 2 : 1;
    return fmtPct(pct, lang, { min: d, max: d });
}

// dd/mm/aaaa en hora de Madrid; sin fecha valida, vacio.
export function fmtDate(iso, lang) {
    const d = new Date(iso);
    if (!iso || Number.isNaN(d.getTime())) return "";
    return new Intl.DateTimeFormat(LOCALE_BY_LANG[norm(lang)], {
        timeZone: "Europe/Madrid", day: "2-digit", month: "2-digit", year: "numeric",
    }).format(d);
}

// Una cifra con su unidad no se parte al final de una linea («250.000» y «€.» en lineas distintas), ni en una marca
// de eje: el Text de recharts parte por los espacios normales cuando la marca no cabe en el ancho del eje.
export const keepTogether = (s) => String(s).replace(/ /g, "\u00a0");

// Decimales para que dos cifras distintas no se lean iguales: 5,29 y 5,34 no pueden salir las dos «5,3».
export function distinctDigits(values, min = 1, max = TABLE_DIGITS) {
    const vs = values.filter((v) => Number.isFinite(v));
    let d = min;
    while (d < max && new Set(vs.map((v) => v.toFixed(d))).size < new Set(vs).size) d += 1;
    return d;
}

// Marca de eje: los decimales los fija el paso, no el valor. El cero de un eje con signo es solo «0».
export function fmtTick(v, lang, unit, decimals) {
    if (unit === "pp") return Number(v) === 0 ? "0" : fmtPp(v, lang, { max: decimals });
    if (unit === "bps") return fmtBps(v, lang, { min: decimals, max: decimals });
    return fmtPct(v, lang, { min: decimals, max: decimals });
}

// ---- Escalas -------------------------------------------------------------------------------------------------

const STEPS = [1, 2, 2.5, 5, 10];
// Para el maximo de un eje simetrico: un 30 se queda en 30 en vez de saltar a 50.
const CEILS = [1, 2, 2.5, 3, 5, 7.5, 10];
const clean = (x) => Number(x.toFixed(10));

function ceilTo(x, candidates) {
    const p = 10 ** Math.floor(Math.log10(x));
    return clean(candidates.find((c) => c * p >= x - 1e-12 * p) * p);
}

export const niceStep = (x) => ceilTo(x, STEPS);

const ticksOf = (min, max, step) => {
    const n = Math.round((max - min) / step);
    return Array.from({ length: n + 1 }, (_, i) => clean(min + i * step));
};

// Eje desde 0 hasta el primer multiplo «bonito» que cubre el valor, en `intervals` tramos o menos.
export function niceAxis(maxValue, intervals = 4, fallback = 10) {
    const v = Number(maxValue) > 0 ? Number(maxValue) : fallback;
    const step = niceStep(v / intervals);
    const max = clean(Math.ceil(clean(v / step)) * step);
    return { max, step, ticks: ticksOf(0, max, step) };
}

// Operaciones: el cero en el centro y el mismo recorrido a los dos lados.
export function symmetricAxis(maxAbs, fallback = 5) {
    const v = Number(maxAbs) > 0 ? Number(maxAbs) : fallback;
    const max = ceilTo(v, CEILS);
    return { max, ticks: [-max, 0, max] };
}

// Eje de un rango (riesgo/rentabilidad): holgura (`pad`, en fraccion del recorrido) a cada lado y marcas exactas. Si
// ningun valor es negativo, el eje no baja de cero.
export function rangeAxis(values, intervals = 4, padFraction = 0.25, minSpan = 0) {
    const vs = values.filter((v) => Number.isFinite(v));
    let lo = Math.min(...vs);
    let hi = Math.max(...vs);
    if (hi - lo < minSpan) {
        const mid = (lo + hi) / 2;
        lo = mid - minSpan / 2;
        hi = mid + minSpan / 2;
    }
    if (hi - lo < 1e-9) {
        const half = Math.max(Math.abs(hi) * 0.1, 0.5) / 2;
        lo -= half;
        hi += half;
    }
    const pad = (hi - lo) * padFraction;
    const step = niceStep((hi - lo + 2 * pad) / intervals);
    let min = clean(Math.floor(clean((lo - pad) / step)) * step);
    const max = clean(Math.ceil(clean((hi + pad) / step)) * step);
    if (min < 0 && vs.every((v) => v >= 0)) min = 0;
    return { min, max, step, ticks: ticksOf(min, max, step) };
}

export function axisDecimals(step) {
    const s = String(clean(step));
    return s.includes(".") ? s.split(".")[1].length : 0;
}

// Ancla de la ventana de una marca en `x` (% del ancho de su pista): a la izquierda de la marca si esta en la mitad
// izquierda, a la derecha si no, y nunca mas ancha que el sitio que le queda hasta el borde de la pista. Asi una
// ventana abierta no ensancha la pagina a 360 px: el texto parte en dos lineas.
export function tooltipPlacement(x) {
    const at = Math.min(100, Math.max(0, Number(x) || 0));
    return at <= 50
        ? { side: "left", offset: at, room: clean(100 - at) }
        : { side: "right", offset: clean(100 - at), room: at };
}

// Posicion de un valor en una escala de 0 a `max`, en % del ancho y sin salirse.
export function pos(v, max) {
    if (!(max > 0)) return 0;
    return Math.min(100, Math.max(0, (Number(v) * 100) / max));
}

// ---- Pesos ---------------------------------------------------------------------------------------------------

export function assetNames(universe) {
    return Object.fromEntries((universe || []).filter((u) => u?.symbol && u.name).map((u) => [u.symbol, u.name]));
}

// Un grupo sin etiqueta del asesor se lee del catalogo; los demas, tal cual los escribio.
export const groupLabel = (group, lang) => (group == null ? tFor(lang)("charts.other") : group);

// El orden del modelo es el que manda; solo se asegura que el grupo sin etiqueta quede el ultimo.
export function weightGroups(weights) {
    const groups = [...(weights?.groups || [])];
    const ordered = [...groups.filter((g) => g.group != null), ...groups.filter((g) => g.group == null)];
    return ordered.map((g, i) => ({
        key: `g${i}`, group: g.group, proposed: g.proposed_pct, current: g.current_pct, items: g.items || [],
    }));
}

export function largestWeight(weights) {
    let best = null;
    for (const g of weights?.groups || []) {
        for (const it of g.items || []) {
            if (!best || it.proposed_pct > best.proposed) best = { symbol: it.symbol, cash: false, proposed: it.proposed_pct, current: it.current_pct };
        }
    }
    const cash = weights?.cash;
    if (cash && (!best || cash.proposed_pct > best.proposed)) best = { symbol: null, cash: true, proposed: cash.proposed_pct, current: cash.current_pct };
    return best;
}

// ---- Operaciones ---------------------------------------------------------------------------------------------

export function tradeRows(trades) {
    const rows = (trades?.items || []).map((it) => ({ symbol: it.symbol, cash: false, delta: it.delta_pct, amount: it.amount_eur ?? null }));
    if (trades?.cash) rows.push({ symbol: null, cash: true, delta: trades.cash.delta_pct, amount: trades.cash.amount_eur ?? null });
    return rows;
}

export function tradeSummary(rows) {
    const assets = rows.filter((r) => !r.cash);
    const largest = rows.reduce((best, r) => (!best || Math.abs(r.delta) > Math.abs(best.delta) ? r : best), null);
    return { buys: assets.filter((r) => r.delta > 0).length, sells: assets.filter((r) => r.delta < 0).length, largest };
}

// ---- Restricciones -------------------------------------------------------------------------------------------

// Barra de uso: con un maximo, el relleno es el valor y la marca el limite, sobre una escala que llega al mayor de
// los dos (asi un incumplimiento se ve pasar la marca). Con un minimo, igual. Con un intervalo, banda y punto.
export function constraintBar(row) {
    if (row.bound === "range") {
        const scale = Math.max(row.max, row.value);
        return { band: [pos(row.min, scale), pos(row.max, scale)], dot: pos(row.value, scale) };
    }
    const scale = Math.max(row.limit, row.value);
    return { fill: pos(row.value, scale), mark: pos(row.limit, scale) };
}

export const constraintSummary = (rows) => ({ ok: rows.filter((r) => r.ok).length, total: rows.length });

// Volatilidad, rotacion y riesgo/rentabilidad van sobre el tramo optimizado: con posiciones fijas no es el 100 %.
export const isPartialSleeve = (share) => share != null && Number(share) < 99.995;

// La parte de la cartera que optimiza el motor, en %: con los decimales que hagan falta para que lo que no es el 100 %
// no se lea «100 %» (un 99,99 % es «99,99 %»). Como fmt_share de backend/services/engine_pdf_charts.py.
export function fmtShare(v, lang) {
    const n = Number(v);
    let d = 1;
    while (d < 4 && n < 100 && Number(n.toFixed(d)) >= 100) d += 1;
    return fmtPct(n, lang, { max: d });
}

// ---- Riesgo y rentabilidad -----------------------------------------------------------------------------------

// En vertical, media vuelta de holgura: la etiqueta de cada punto (dos lineas) cabe por encima o por debajo.
// `minSpanPct` (puntos porcentuales, por defecto ninguno) evita que una diferencia minima se dibuje como una pendiente
// pronunciada: lo usa la pagina publica; la app no lo pasa.
// Fraccion del eje Y que queda bajo el punto mas bajo: su etiqueta (dos lineas) va debajo y, sin este hueco, el eje
// la cortaba cuando la cartera actual rentaba cerca de 0 y el eje empezaba en 0.
export const RISK_LABEL_ROOM = 0.2;

export function riskReturnAxes(rr, { minSpanPct = 0 } = {}) {
    const points = [rr.proposed, rr.current].filter(Boolean);
    const rets = points.map((p) => p.return_pct);
    const y = rangeAxis(rets, 4, 0.5, minSpanPct);
    const lowest = Math.min(...rets.filter((v) => Number.isFinite(v)));
    let min = y.min;
    while ((lowest - min) / (y.max - min) < RISK_LABEL_ROOM) min = clean(min - y.step);
    return {
        x: rangeAxis(points.map((p) => p.vol_pct), 4, 0.35, minSpanPct),
        y: min === y.min ? y : { ...y, min, ticks: ticksOf(min, y.max, y.step) },
    };
}

// ---- Comparacion cuantica ------------------------------------------------------------------------------------

const METHODS = ["exact", "classical", "quantum", "best_of_samples"];
// Por debajo de esto (pb/año) una distancia es redondeo: el clasico da el optimo exacto.
const ZERO_BPS = 0.005;

export function distanceRows(q) {
    const byMethod = Object.fromEntries((q?.distance || []).map((d) => [d.method, d]));
    return METHODS.filter((m) => byMethod[m]).map((m) => ({ method: m, value: byMethod[m].dist_exact_bps }));
}

export function probabilityRows(q) {
    const p = q?.probability || {};
    return [
        { series: "random", value: p.random ?? null, low: null },
        { series: "quantum", value: p.quantum ?? null, low: p.quantum_ci95_low ?? null },
    ];
}

// En % (no en tanto por uno), para pintarlo con fmtTick.
export function probabilityAxis(q) {
    const p = q?.probability || {};
    const vs = [p.quantum, p.quantum_ci95_low, p.random].filter((v) => v != null).map((v) => v * 100);
    return niceAxis(vs.length ? Math.max(...vs) : 0, 2, 5);
}

// Las medidas del fichero cuantico: `shots` si lo trae, si no el total de recuentos. Un solo sitio para la linea de
// datos y para la fila «Mejor de las … medidas».
export function quantumShots(q) {
    const m = q?.meta || {};
    const v = m.shots ?? m.n_counts ?? null;
    return v != null && Number.isFinite(Number(v)) && Number(v) > 0 ? Number(v) : null;
}

// Probabilidad de que, eligiendo `shots` carteras validas al azar, al menos una sea la optima.
export function randomHitChance(q) {
    const p = q?.probability?.random;
    const shots = quantumShots(q);
    if (p == null || shots == null || !(p >= 0)) return null;
    return 1 - (1 - Math.min(1, Number(p))) ** shots;
}
// Por encima de esto, que la mejor medida de el optimo es lo esperable por puro azar.
const RANDOM_ALSO = 0.99;

// «Cómo leerlo» segun lo que dicen los numeros, no segun lo que querriamos que dijeran.
export function howToRead(q) {
    const rows = Object.fromEntries(distanceRows(q).map((r) => [r.method, r.value]));
    const out = [];
    if (q?.case === "none" || rows.quantum == null) out.push({ key: "noValid" });
    if (rows.exact != null && rows.classical != null) {
        out.push(Math.abs(rows.classical) < ZERO_BPS ? { key: "classicalExact" } : { key: "classicalGap", dist: rows.classical });
    }
    if (q?.case === "higher" && rows.quantum != null) out.push({ key: "higher" });
    // Solo si la mejor medida dio de verdad el optimo: la frase lo da por hecho.
    const best = rows.best_of_samples;
    const chance = randomHitChance(q);
    if (best != null && Math.abs(best) < ZERO_BPS && chance != null && chance > RANDOM_ALSO) out.push({ key: "randomAlso", shots: quantumShots(q) });
    out.push({ key: "research" });
    return out;
}

export const regionLabel = (code, lang) => (code ? tFor(lang)(`charts.quantum.region.${code}`, { defaultValue: code }) : "");

// El resumen de una comparacion tal como llega de la API, reducido a lo que tiene la forma de quantum_charts: un
// metodo desconocido, una cifra que no es un numero finito o una probabilidad fuera de [0, 1] se descartan, y el
// limite inferior sin su probabilidad tampoco se pinta. Si no queda nada que dibujar, null: la tarjeta se queda como
// estaba, con su estado y su informe.
const finite = (v) => (typeof v === "number" && Number.isFinite(v) ? v : null);
const isObject = (v) => Boolean(v) && typeof v === "object" && !Array.isArray(v);
const QUANTUM_CASES = ["equal", "worse", "higher", "none"];

export function safeQuantumCharts(c) {
    if (!isObject(c)) return null;
    const distance = (Array.isArray(c.distance) ? c.distance : [])
        .filter((d) => isObject(d) && METHODS.includes(d.method) && finite(d.dist_exact_bps) != null)
        .map((d) => ({ method: d.method, dist_exact_bps: d.dist_exact_bps }));
    const p = isObject(c.probability) ? c.probability : {};
    const prob = (v) => (finite(v) != null && v >= 0 && v <= 1 ? v : null);
    const quantum = prob(p.quantum);
    const probability = {
        quantum, quantum_ci95_low: quantum != null ? prob(p.quantum_ci95_low) : null, random: prob(p.random), feasible_rate: prob(p.feasible_rate),
    };
    if (!distance.length && quantum == null && probability.random == null) return null;
    const m = isObject(c.meta) ? c.meta : {};
    const text = (v) => (typeof v === "string" && v ? v : null);
    return {
        distance,
        probability,
        meta: { n: finite(m.n), k: finite(m.k), shots: finite(m.shots), n_counts: finite(m.n_counts), region: text(m.region), backend: text(m.backend) },
        case: QUANTUM_CASES.includes(c.case) ? c.case : null,
    };
}

// ---- Comparacion de dos calculos -----------------------------------------------------------------------------

// Pesos de dos calculos, A y B ({ weights: {simbolo: %}, universe }), activo por activo. Union de los dos: primero el
// orden del universo de A, despues lo que solo trae el de B y al final lo que tuviera peso sin estar en ninguno. Un
// activo que falta en un calculo pesa 0 en el; uno que pesa 0 en los dos no sale. Por debajo de 0,01 pp no hay cambio.
const symbolOf = (u) => (u && typeof u === "object" ? u.symbol : u);

export function compareWeightRows(a, b) {
    const wa = a?.weights || {};
    const wb = b?.weights || {};
    const order = [...(a?.universe || []).map(symbolOf), ...(b?.universe || []).map(symbolOf), ...Object.keys(wa), ...Object.keys(wb)];
    const rows = [];
    for (const symbol of new Set(order.filter(Boolean))) {
        const x = Number(wa[symbol]) || 0;
        const y = Number(wb[symbol]) || 0;
        if (!x && !y) continue;
        const delta = clean(Number((y - x).toFixed(4))) || 0;
        rows.push({ symbol, a: x, b: y, delta, changed: Math.abs(delta) >= 0.01, only: !x ? "b" : !y ? "a" : null });
    }
    const max = rows.reduce((m, r) => Math.max(m, r.a, r.b), 0);
    return { rows, max, changed: rows.filter((r) => r.changed).length };
}
